import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { DatapointsDataService, DataPoint } from '@services/datapoints-data.service';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, takeUntil } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { ListViewComponent } from '@syncfusion/ej2-angular-lists';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { ConfirmDialogComponent } from 'src/app/dialogs/confirm-dialog/confirm-dialog.component';

/**
 * Component
 */
@Component({
	selector: 'app-organisation-datapoints',
	templateUrl: './organisation-datapoints.component.html',
	styleUrls: ['./organisation-datapoints.component.scss']
})
export class OrganisationDatapointsComponent implements OnInit, OnDestroy {

	/** Column Names */
	public fields: Object = { text: 'DPTitle', id: 'DataPointID' };

	/** Form Group */
	dialogForm: FormGroup;

	/** Sample List */
	@ViewChild('samplelist') public samplelist: ListViewComponent;

	/** listview Instance */
	@ViewChild('listviewInstance') public listviewInstance: ListViewComponent;

	/** Datapoints */
	public dataPoints: MatTableDataSource<any>
	/** Displayed Datapoints */
	public displayedDatapoints: any

	/** Selected Indexes */
	selectedIndexes: number[] = [];

	/** Account GUID */
	public accountGUID: string;

	/** Organisation UID */
	public organisationUID: string;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/**
	 * Default Constructor
	 * @param fb Form Builder
	 * @param dialogRef Referense to the Dialog box
	 * @param datapointService DatapointsDataService
	 * @param globalFunctions GlobalFunctions
	 * @param siteSubscriptions siteSubscriptions
	 * @param dialogData  object interface
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<OrganisationDatapointsComponent>,
		private datapointService: DatapointsDataService,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		@Inject(MAT_DIALOG_DATA) public dialogData: {},
	) { }

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.dialogForm = this.fb.group({
			dataPointIDs: [""],
			dataPointNames: [""]
		});

		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, OrganisationUID,]) => !!selectedAccountGUID && !!OrganisationUID)
		).subscribe({
			next: ([selectedAccountGUID, OrganisationUID]) => {
				this.accountGUID = selectedAccountGUID;
				this.organisationUID = OrganisationUID;
				this.GetDataPoints(this.accountGUID, this.organisationUID)

			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Get the Data Points for the Asset
	 * @param asset
	 * @param dateStart
	 * @param dateEnd
	 * @returns
	 */
	GetDataPoints(accountID: string, orgUID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.datapointService.getDataPoints$(accountID, orgUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((dataPoints) => !!dataPoints),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (dataPoints: any) => {
				try {
					this.dataPoints = new MatTableDataSource(dataPoints);
					this.displayedDatapoints = this.dataPoints.filteredData
				} catch (error) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getDataPoints.next", false);
				}
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getDataPoints$", false);
				}
			},
			//complete: () => { console.log("this.AssetsDataService.getDataPoints$.subscribe: Complete"); }
		});

	}

	/**
	 * Get the Selected Datapoint IDs
	 * @param event
	 */
	onSelectionChange(event: any) {
		try {

			let selectedItems = this.samplelist.getSelectedItems()
			let selectedSensorDataPoints: DataPoint[] = selectedItems.data as unknown as DataPoint[];
			let dataPointIDs = selectedSensorDataPoints.map((x: DataPoint) => x.DataPointID);
			let dataPointNames = selectedSensorDataPoints.map((x: DataPoint) => x.DPTitle);

			this.dialogForm.setValue({
				'dataPointIDs': dataPointIDs.join(', '),
				'dataPointNames': dataPointNames.join(', ')
			})

		} catch (error) {
			this.globalFunctions.HandleError(error, "Error: onSelectionChange", false);
		}

	}

	/**
	 * Edit the Location details
	 * @param imageURL
	 */
	viewImage(imageURL: string): void {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * Key up event from the search input on HTML
	 * @param event HTMLInputElement
	 */
	applyFilter(event: Event) {
		try {
			const filterValue = (event.target as HTMLInputElement).value.toLowerCase();

			this.dataPoints.filterPredicate = (data, filter: string) => {
				/** Build a string per row to filter and convert to lower case */
				// const textToSearch = (data.PublicID + data.SensorName + data.PVName + data.PVDescription).toLowerCase() || '';
				const textToSearch = (data.SensorName + data.PVName + data.PVDescription).toLowerCase() || '';
				return textToSearch.indexOf(filter) !== -1;
			};

			this.dataPoints.filter = filterValue.trim().toLowerCase();

			this.displayedDatapoints = this.dataPoints.filteredData

			if (this.displayedDatapoints.length == 0) {
				this.globalFunctions.DisplaySnackBar({
					message: "No matches for '" + filterValue.trim().toLowerCase() + "'",
					type: SnackType.Alert,
					duration: 1000
				})
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "Error filtering list") }
	}

	/**
	 * selectDataPoint
	 * @param DataPointID
	 */
	selectDataPoint(DataPointID: number) {

		// this.dialogForm.setValue({ 'dataPointID': DataPointID })

	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});	
		} else {
			this.dialogRef.close();
		}
	}
	
}
